import React, { useEffect, useState } from "react";

import {
  Box,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  DialogContentText
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

import AppButton from "../ui/AppButton";
import PageHeader from "../ui/PageHeader";
import DeleteDialog from "../modals/DeleteDialog";
import RoleTable from "../components/roles/RoleTable";
import RoleDialog from "../components/roles/RoleDialog";

import { createRole, updateRole, deleteRole } from "../services/roleService";
import { useRoles } from "../context/RolesContext";

function RoleManagement() {
  const { roles, loading, refreshRoles } = useRoles();
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    // Keep only valid selected ids after the shared role list changes.
    setSelectedRoleIds((prev) => {
      const validIds = new Set(roles.filter((role) => !role.is_system).map((role) => role.role_id));
      return prev.filter((id) => validIds.has(id));
    });
  }, [roles]);

  const filteredRoles = roles.filter((role) =>
    role.role_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      if (selectedRole) {
        await updateRole(selectedRole.role_id, data);
      } else {
        await createRole(data);
      }

      setDialogOpen(false);
      setSelectedRole(null);

      await refreshRoles();

      setSnackbar({ open: true, message: "Role saved successfully", severity: "success" });
    } catch (error) {
      console.error("Role Save Error:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || "Failed to save role",
        severity: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole || isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteRole(selectedRole.role_id);

      setDeleteOpen(false);
      setSelectedRole(null);

      await refreshRoles();

      setSnackbar({ open: true, message: "Role deleted successfully", severity: "success" });
    } catch (error) {
      console.error("Role Delete Error:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || "Failed to delete role",
        severity: "error"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRoleIds.length === 0 || isBulkDeleting) {
      return;
    }

    try {
      setIsBulkDeleting(true);

      let successCount = 0;
      let failedCount = 0;

      for (const roleId of selectedRoleIds) {
        try {
          await deleteRole(roleId);
          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkDeleteOpen(false);
      setSelectedRoleIds([]);

      await refreshRoles();

      if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} role(s) deleted successfully`,
          severity: "success"
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} deleted, ${failedCount} failed`,
          severity: "warning"
        });
      }
    } catch (error) {
      console.error("Role Bulk Delete Error:", error);
      setBulkDeleteOpen(false);
      setSnackbar({ open: true, message: "Bulk delete failed", severity: "error" });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", p: 3 }}>
      <PageHeader
        title="Role Management"
        subtitle="Create custom roles and control which pages each role can access."
        actions={(
          <>
            <AppButton
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              loading={isBulkDeleting}
              onClick={() => setBulkDeleteOpen(true)}
              disabled={selectedRoleIds.length === 0}
            >
              {isBulkDeleting ? "Deleting..." : `Delete All (${selectedRoleIds.length})`}
            </AppButton>

            <AppButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedRole(null);
                setDialogOpen(true);
              }}
            >
              Add Role
            </AppButton>
          </>
        )}
      />

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 1.5,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search role name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputBase-root": {
              height: 38,
            },
          }}
        />
      </Box>

      <RoleTable
        roles={filteredRoles}
        loading={loading}
        selectedIds={selectedRoleIds}
        onSelectionChange={setSelectedRoleIds}
        onEdit={(role) => {
          setSelectedRole(role);
          setDialogOpen(true);
        }}
        onDelete={(role) => {
          setSelectedRole(role);
          setDeleteOpen(true);
        }}
      />

      <RoleDialog
        open={dialogOpen}
        role={selectedRole}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setDialogOpen(false);
          setSelectedRole(null);
        }}
        onSave={handleSave}
      />

      <DeleteDialog
        open={deleteOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteOpen(false);
            setSelectedRole(null);
          }
        }}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        title="Delete Role"
        message="Are you sure you want to delete the role"
        itemName={selectedRole?.role_name?.replace(/_/g, " ")}
        extra={(
          <DialogContentText sx={{ mt: 1 }}>
            Employees currently assigned this role will keep the value but lose page access
            until reassigned.
          </DialogContentText>
        )}
      />

      <DeleteDialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (isBulkDeleting) {
            return;
          }
          setBulkDeleteOpen(false);
        }}
        onConfirm={handleBulkDelete}
        isDeleting={isBulkDeleting}
        title="Delete Selected Roles"
        message="Are you sure you want to delete all selected roles?"
        itemName={`Selected: ${selectedRoleIds.length}`}
        extra={(
          <DialogContentText sx={{ mt: 1 }}>
            Employees currently assigned a deleted role will keep the value but lose page access
            until reassigned.
          </DialogContentText>
        )}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default RoleManagement;
